/* Oreganix cart drawer extras: reservation timer, discount code field, automatic gifts, upsells
   and terms checkbox. Talks only to this store's Cart API and recommendations endpoint. */

(() => {
  const TIMER_KEY = 'oxCartTimerEnd';
  const TERMS_KEY = 'oxCartTerms';
  const GIFT_KEY = 'oxCartGiftSync';

  const storage = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {}
    },
  };

  const cartUrl = (path) => `${window.routes?.cart_url || '/cart'}${path}`;

  // Sections to request with a cart mutation so the drawer can be redrawn from the response.
  const sectionsToRender = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
  };

  const postCart = async (path, body) => {
    const response = await fetch(cartUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionsToRender(), sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart error');
    return state;
  };

  // Redraw the cart UI after a mutation made outside Dawn's own cart components.
  const refreshCart = (state) => {
    if (document.getElementById('main-cart-items')) {
      window.location.reload();
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    if (!state?.sections) return;
    (drawer ? drawer.getSectionsToRender() : [{ id: 'cart-icon-bubble' }]).forEach((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = state.sections[section.id];
      if (!target || !html) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    });
    if (drawer) {
      if (typeof state.item_count === 'number') drawer.classList.toggle('is-empty', state.item_count === 0);
      // Same as Dawn's renderContents: the overlay is replaced, so rebind its close handler.
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    }
  };

  window.oxCart = { postCart, refreshCart };

  if (!customElements.get('ox-cart-timer')) {
    customElements.define(
      'ox-cart-timer',
      class OxCartTimer extends HTMLElement {
        connectedCallback() {
          this.text = this.querySelector('[data-timer-text]');
          const duration = Number(this.dataset.duration) || 300;
          let end = Number(storage.get(TIMER_KEY)) || 0;
          // The drawer re-renders on every cart change; keep counting from the first render.
          if (!end || end < Date.now()) {
            end = Date.now() + duration * 1000;
            storage.set(TIMER_KEY, String(end));
          }
          this.end = end;
          this.tick();
          this.interval = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.interval);
        }

        tick() {
          const left = Math.max(0, Math.round((this.end - Date.now()) / 1000));
          if (left === 0) {
            clearInterval(this.interval);
            if (this.dataset.expired) {
              this.text.textContent = this.dataset.expired;
              return;
            }
          }
          const mm = String(Math.floor(left / 60)).padStart(2, '0');
          const ss = String(left % 60).padStart(2, '0');
          this.text.innerHTML = this.text.dataset.template.replaceAll('[timer]', `${mm}:${ss}`);
        }
      }
    );
  }

  if (!customElements.get('ox-cart-discount')) {
    customElements.define(
      'ox-cart-discount',
      class OxCartDiscount extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input');
          this.error = this.querySelector('[data-error]');
          this.querySelector('[data-apply]')?.addEventListener('click', () => this.apply());
          this.input?.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.apply();
            }
          });
          this.querySelectorAll('[data-remove]').forEach((button) =>
            button.addEventListener('click', () => this.update(this.currentCodes().filter((c) => c !== button.dataset.remove)))
          );
        }

        currentCodes() {
          return [...this.querySelectorAll('[data-remove]')].map((b) => b.dataset.remove);
        }

        apply() {
          const code = this.input.value.trim();
          if (!code) {
            this.error.hidden = false;
            return;
          }
          this.error.hidden = true;
          this.update([...new Set([...this.currentCodes(), code])]);
        }

        async update(codes) {
          this.classList.add('is-loading');
          try {
            refreshCart(await postCart('/update.js', { discount: codes.join(',') }));
          } catch (error) {
            console.error(error);
            this.error.hidden = false;
          } finally {
            this.classList.remove('is-loading');
          }
        }
      }
    );
  }

  /* ---------- Automatic gifts ---------- */
  // Liquid works out which gift lines to add or remove; this applies it once per render.
  if (!customElements.get('ox-cart-gift-sync')) {
    customElements.define(
      'ox-cart-gift-sync',
      class OxCartGiftSync extends HTMLElement {
        connectedCallback() {
          const add = (this.dataset.add || '').split(',').filter(Boolean);
          let updates = {};
          try {
            updates = JSON.parse(this.dataset.updates || '{}');
          } catch (e) {}
          if (!add.length && !Object.keys(updates).length) return;

          // Stop retrying the same change (e.g. gift out of stock) after two failed attempts.
          const signature = `${this.dataset.add}|${this.dataset.updates}`;
          let guard = {};
          try {
            guard = JSON.parse(storage.get(GIFT_KEY) || '{}');
          } catch (e) {}
          const attempts = guard.signature === signature ? guard.attempts : 0;
          if (attempts >= 2) return;
          storage.set(GIFT_KEY, JSON.stringify({ signature, attempts: attempts + 1 }));

          // Let Dawn finish its own render before changing the cart again.
          setTimeout(() => this.sync(add, updates));
        }

        async sync(add, updates) {
          try {
            let state = null;
            if (Object.keys(updates).length) state = await postCart('/update.js', { updates });
            if (add.length) {
              state = await postCart('/add.js', {
                items: add.map((id) => ({ id: Number(id), quantity: 1, properties: { _ox_gift: '1' } })),
              });
            }
            storage.set(GIFT_KEY, '{}');
            refreshCart(state);
          } catch (error) {
            console.error(error);
          }
        }
      }
    );
  }

  /* ---------- Upsells ---------- */
  const recommendationCache = new Map();

  if (!customElements.get('ox-cart-upsells')) {
    customElements.define(
      'ox-cart-upsells',
      class OxCartUpsells extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => this.onClick(event));
          this.addEventListener('change', (event) => this.onChange(event));
          if (this.dataset.url) this.load();
        }

        async load() {
          const url = this.dataset.url;
          try {
            if (!recommendationCache.has(url)) {
              recommendationCache.set(
                url,
                fetch(url).then((response) => (response.ok ? response.text() : ''))
              );
            }
            const html = await recommendationCache.get(url);
            const track = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-upsell-track]');
            if (!track || !track.children.length) return;
            this.querySelector('[data-upsell-track]').replaceWith(track);
            this.hidden = false;
          } catch (error) {
            recommendationCache.delete(url);
            console.error(error);
          }
        }

        onChange(event) {
          // cart-drawer-items treats any bubbling change as a quantity edit.
          event.stopPropagation();
          const select = event.target.closest('[data-upsell-variant]');
          if (!select) return;
          const card = select.closest('[data-upsell]');
          const option = select.selectedOptions[0];
          card.querySelector('[data-upsell-add]').dataset.variantId = select.value;
          card.querySelector('[data-upsell-price]').textContent = option.dataset.price;
          const compare = card.querySelector('[data-upsell-compare]');
          compare.textContent = option.dataset.compare;
          compare.hidden = !option.dataset.compare;
        }

        onClick(event) {
          const track = this.querySelector('[data-upsell-track]');
          if (event.target.closest('[data-upsell-prev]')) track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
          if (event.target.closest('[data-upsell-next]')) track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
          const button = event.target.closest('[data-upsell-add]');
          if (button) this.add(button);
        }

        async add(button) {
          button.classList.add('loading');
          button.disabled = true;
          try {
            refreshCart(await postCart('/add.js', { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }));
          } catch (error) {
            console.error(error);
            button.disabled = false;
            button.classList.remove('loading');
          }
        }
      }
    );
  }

  /* ---------- Terms checkbox ---------- */
  if (!customElements.get('ox-cart-terms')) {
    customElements.define(
      'ox-cart-terms',
      class OxCartTerms extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('[data-terms]');
          this.error = this.querySelector('[data-terms-error]');
          // The drawer is redrawn on every cart change; keep the tick.
          this.input.checked = storage.get(TERMS_KEY) === '1';
          this.input.addEventListener('change', () => {
            storage.set(TERMS_KEY, this.input.checked ? '1' : '0');
            if (this.input.checked) this.error.hidden = true;
          });
        }

        get accepted() {
          return this.input.checked;
        }

        showError() {
          this.error.hidden = false;
          this.input.focus();
        }
      }
    );

    // Capture phase: runs before the checkout button submits its form.
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[name="checkout"]');
        if (!button) return;
        let scope = button.parentElement;
        while (scope && !scope.querySelector('ox-cart-terms')) scope = scope.parentElement;
        const terms = scope?.querySelector('ox-cart-terms');
        if (!terms || terms.accepted) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        terms.showError();
      },
      true
    );
  }
})();
